import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, catchError, map, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IBranchModel } from './../logic/branch/models/branch.model';
import { ApiResponse, ApiData } from '../common/api-response.types';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BranchesService {
  private readonly BASE_URL: string = environment.BASE_URL;

  private readonly _branches$: BehaviorSubject<IBranchModel[]> =
    new BehaviorSubject<IBranchModel[]>([]);

  private readonly sucursalId$ = new BehaviorSubject<number>(0);

  /** Emite cuando se quiere abrir el diálogo de reserva desde otro componente */
  readonly openReserveDialog$ = new Subject<void>();

  triggerReserveDialog(): void {
    this.openReserveDialog$.next();
  }

  constructor(private readonly _httpClient: HttpClient) {}

  get branches$(): Observable<IBranchModel[]> {
    return this._branches$.asObservable();
  }

  set sucursalId(id: number) {
    this.sucursalId$.next(id);
  }

  get sucursalId(): Observable<number> {
    return this.sucursalId$.asObservable();
  }

  /**
   * Obtiene la sucursal seleccionada a partir del backend y actualiza el cache local.
   * Si no hay una sucursal guardada o no existe en el listado actual, selecciona la primera.
   * @param forceRefresh Se conserva por compatibilidad con llamadas existentes.
   * @returns Observable con la sucursal seleccionada o null si no hay sucursales disponibles
   */
  getBranchSelected(
    forceRefresh: boolean = false,
  ): Observable<IBranchModel | null> {
    return this.getAll(forceRefresh).pipe(
      map((branches) => {
        if (branches.length === 0) {
          return null;
        }

        // Si hay una sucursal guardada con ID, intentar encontrarla
        const branchId = this.getBranchIdFromCache();
        const cachedBranch = this.getBranchFromCache();
        const selectedBranch =
          (branchId && branches.find((b) => b.id === branchId)) ||
          (cachedBranch?.id
            ? branches.find((b) => b.id === cachedBranch.id)
            : undefined) ||
          branches[0];
        this.saveBranchToCache(selectedBranch);
        this.saveBranchIdToCache(selectedBranch.id);
        return selectedBranch;
      }),
    );
  }

  /**
   * Obtiene la sucursal seleccionada desde localStorage
   * @returns Sucursal seleccionada o null si no existe
   */
  private getBranchFromCache(): IBranchModel | null {
    try {
      const stored = localStorage.getItem('branch');
      if (!stored) {
        return null;
      }
      const branch = JSON.parse(stored);
      return branch?.id ? branch : null;
    } catch (error) {
      console.error('Error al leer branch del cache:', error);
      return null;
    }
  }

  /**
   * Obtiene el ID de la sucursal seleccionada desde localStorage
   * @returns ID de la sucursal o null si no existe
   */
  private getBranchIdFromCache(): number | null {
    try {
      const stored = localStorage.getItem('branchId');
      if (!stored) {
        return null;
      }
      const branchId = Number.parseInt(stored, 10);
      return !Number.isNaN(branchId) && branchId > 0 ? branchId : null;
    } catch (error) {
      console.error('Error al leer branchId del cache:', error);
      return null;
    }
  }

  /**
   * Guarda la sucursal seleccionada en localStorage
   * @param branch Sucursal a guardar
   */
  private saveBranchToCache(branch: IBranchModel): void {
    try {
      localStorage.setItem('branch', JSON.stringify(branch));
    } catch (error) {
      console.error('Error al guardar branch en cache:', error);
    }
  }

  /**
   * Guarda el ID de la sucursal seleccionada en localStorage
   * @param branchId ID de la sucursal a guardar
   */
  private saveBranchIdToCache(branchId: number): void {
    try {
      localStorage.setItem('branchId', branchId.toString());
    } catch (error) {
      console.error('Error al guardar branchId en cache:', error);
    }
  }

  /**
   * Establece la sucursal seleccionada y la guarda en cache
   * @param branch Sucursal a seleccionar
   */
  setBranchSelected(branch: IBranchModel): void {
    this.saveBranchToCache(branch);
    this.saveBranchIdToCache(branch.id);
    this.sucursalId = branch.id;
  }

  checkInventoryAvailability(
    branchId: number,
    currencyCode: string,
    requestedAmount: number,
  ): Observable<boolean> {
    return this._httpClient
      .post<ApiResponse<{ hasEnoughStock: boolean }>>(
        `${this.BASE_URL}/branches/${branchId}/inventory/check`,
        { currencyCode, requestedAmount },
      )
      .pipe(map((res) => this.extractHasEnoughStock(res.data)));
  }

  private extractHasEnoughStock(
    data: ApiData<{ hasEnoughStock: boolean }> | undefined,
  ): boolean {
    if (!data || Array.isArray(data) || !('hasEnoughStock' in data)) {
      return false;
    }

    return Boolean(data.hasEnoughStock);
  }

  /**
   * Obtiene todas las sucursales desde el backend y las guarda en cache.
   * Si la petición falla, usa el cache local como respaldo.
   * @param forceRefresh Se conserva por compatibilidad con llamadas existentes.
   * @returns Observable con el array de sucursales
   */
  getAll(forceRefresh: boolean = false): Observable<IBranchModel[]> {
    return this._httpClient.get<any>(`${this.BASE_URL}/branches`).pipe(
      map((response) => {
        const branches = (response?.data || response || []).map(
          (branch: any) => ({
            ...branch,
            name: branch.branchName,
          }),
        );

        this.saveBranchesToCache(branches);
        return branches;
      }),
      catchError((error) => {
        const cachedBranches = this.getBranchesFromCache();
        if (cachedBranches !== null && cachedBranches.length > 0) {
          return of(cachedBranches);
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene las sucursales desde localStorage
   * @returns Array de sucursales o null si no existe
   */
  private getBranchesFromCache(): IBranchModel[] | null {
    try {
      const stored = localStorage.getItem('branches');
      if (!stored) {
        return null;
      }
      const branches = JSON.parse(stored);
      return Array.isArray(branches) && branches.length > 0 ? branches : null;
    } catch (error) {
      console.error('Error al leer branches del cache:', error);
      return null;
    }
  }

  /**
   * Guarda las sucursales en localStorage
   * @param branches Array de sucursales a guardar
   */
  private saveBranchesToCache(branches: IBranchModel[]): void {
    try {
      localStorage.setItem('branches', JSON.stringify(branches));
    } catch (error) {
      console.error('Error al guardar branches en cache:', error);
    }
  }

  /**
   * Limpia el cache de sucursales
   */
  clearBranchesCache(): void {
    localStorage.removeItem('branches');
  }
}
